import { Fragment, useCallback, useEffect, useState } from 'react';
import { Stack, Typography, Avatar, IconButton, Dialog, DialogContent, Button, DialogActions, Box, Divider, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { uniqueId } from 'lodash';
import PublicAPI from '@/api/TreedisPublicDAO';
import socket from '@/components/util/socket';
import { formData } from '@/utils/formData';
import { NoteForm } from './NoteForm';
import { User } from '@/_models/User';
import { getInitials } from '@/utils/getInitials';
import { NoteReporterBar } from './NoteReporterBar';
import { Gallery } from '@/components/common/Gallery/Gallery';
import WaveSurferPlayer from '@/components/common/AudioPlayer/WaveSurferPlayer';
import { CompleteNoteModal } from './modals/CompleteNoteModal';
import { DeleteNoteModal } from './modals/DeleteNoteModal';
import Icon from '@/units/Icon';
import moment from 'moment';
import { SAPDetails } from './SAPDetails';
import { EditMenu } from './EditMenu';
import { getBaseURL } from '@/components/util';


const formatDate = (dateString: string) => {
  const date = moment(dateString);

  if (!date.isValid()) {
    console.log('thread formatDate is invalid for ', dateString);
    return '';
  }

  return date.format('MMMM D, h:mm A');
};

export const Thread = ({
  rtl,
  selectedThread,
  setSelectedThread,
  updateMessages,
  newThread,
  setNewThread,
  tour,
  showcase,
  addThreadToSpace,
  showCaseTemp,
  userId,
  collaborators,
  isTourAdmin,
  setThreads,
  threads,
  onThreadItemSelect,
  setIsAdding,
  controls,
  isSAPActive,
  editMode,
  setEditMode,
  setAddThreadClicked,
  onCompleteNote,
  onCloseDrawer,
  removeThreadFromSpace,
  mediaUrls,
  setMediaUrls,
  anchorEl,
  setAnchorEl,
  openedMenuId,
  setOpenedMenuId,
  isDeleteNoteModalOpen,
  setIsDeleteNoteModalOpen,
  handleEdit,
  isMenuOpen,
  getLocation,
  isRtl,
  isGuest,
}: {
  rtl: boolean;
  selectedThread: any;
  setSelectedThread: (n: any) => void;
  updateMessages: (n: any, m: any) => void;
  newThread: any;
  setNewThread: (n: any) => void;
  tour: any;
  closeDrawer: () => void;
  showcase: any;
  addThreadToSpace: (thread: any) => Promise<void>;
  showCaseTemp: any;
  userId: number | null;
  collaborators: User[];
  isTourAdmin: boolean;
  resetNewMessagesCount: () => void;
  setThreads: (threads: any) => void;
  threads: any[];
  toggleThreadVisibility: (thread: any, isVisible: boolean) => void;
  onThreadItemSelect: (t: any) => void;
  setIsAdding: (value: boolean) => void;
  controls: any;
  isSAPActive: boolean;
  editMode: boolean;
  setEditMode: (value: boolean) => void;
  setAddThreadClicked?: (value: boolean) => void;
  onCompleteNote: (noteId: any, value?: any) => void;
  onCloseDrawer: () => void;
  removeThreadFromSpace: (note: any) => void;
  mediaUrls: any[];
  setMediaUrls: (args: any) => void;
  anchorEl: null | HTMLElement;
  setAnchorEl: (value: null | HTMLElement) => void;
  openedMenuId: number | null;
  setOpenedMenuId: (value: number | null) => void;
  isDeleteNoteModalOpen: boolean;
  setIsDeleteNoteModalOpen: (value: boolean) => void;
  handleEdit: () => void;
  isMenuOpen: boolean;
  getLocation: () => string;
  isRtl: boolean;
  isGuest: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [noteFormat, setNoteFormat] = useState<string>('message');
  const [messages, setMessages] = useState([]);
  const [SAPData, setSAPData] = useState({});
  const [files, setFiles] = useState([]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [isCompleted, setIsCompleted] = useState(selectedThread?.isCompleted || false);

  const handleToggle = (id) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSAPFieldOptions = async () => {
    const result = await PublicAPI.getSAPOptions(tour.id);
    if (result?.message === 'Success') return result.SAPFieldsOptions;
    return [];
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setIsDialogOpen(false);
    const messageToDelete = messages.find((m) => m.id === openedMenuId);
    if (!messageToDelete) {
      toast.error('Could not delete message');
    }

    const result = await PublicAPI.deleteThreadMessage({
      data: { messageId: messageToDelete.id },
    });

    if (result?.message === 'Success') {
      toast.error('Message deleted');
      socket.emit('message_deleted', { id: messageToDelete.id, threadId: messageToDelete.threadId, userId: messageToDelete.userId });
    }
    setThreads((prevThreads) => {
      const foundThread = prevThreads.find((thread) => thread.id === messageToDelete.threadId);
      if (foundThread) {
        const updatedThreads = prevThreads.map((thread) => {
          if (thread.id === foundThread.id) {
            const messages = thread.messages.filter((message) => message.id !== messageToDelete.id);
            return { ...thread, messages };
          }
          return thread;
        });

        setSelectedThread((prevSelectedThread) => {
          if (prevSelectedThread?.id === messageToDelete.threadId) {
            const updatedMessages = prevSelectedThread.messages.filter((message) => message.id !== messageToDelete.id);
            return { ...prevSelectedThread, messages: updatedMessages };
          }
          return prevSelectedThread;
        });

        return updatedThreads;
      }
      return prevThreads;
    });

    setAnchorEl(null);
    setOpenedMenuId(null);
    setMediaUrls([]);
    setIsLoading(false);
    return result?.message;
  };

  const foundUser = collaborators?.find((c) => c.id === userId);
  const threadUser = collaborators?.find((c) => c.id === selectedThread?.userId);

  useEffect(() => {
    if (selectedThread && selectedThread.messages) {
      const threadMessages = selectedThread.messages.sort((a, b) => {
        const dateA: Date = new Date(a.createdAt);
        const dateB: Date = new Date(b.createdAt);

        return dateA.getTime() - dateB.getTime();
      });
      setMessages(threadMessages);
      setIsCompleted(selectedThread?.isCompleted);
    } else {
      setMessages([]);
      setIsCompleted(false);
    }
  }, [selectedThread, selectedThread?.messages, selectedThread?.isCompleted]);

  const handleAdd = async ({ title, description, assignee, messageId, isMainMessage }) => {
    let resultMessage = '';
    const createdAt = moment().format('YYYYMMDD HHmm');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    description = description || '';
    let mentionedUsers = [];
    const tourLink = getTourLink();
    if (description) {
      const mentionedUsernames = [...description.matchAll(/<span[^>]*>.*?@([a-zA-Z0-9_ ]+)<\/span>/g)].map((match) => match[1]);
      mentionedUsers.push(...collaborators.filter((collaborator) => mentionedUsernames.includes(collaborator.name)));
    }
    if (newThread) {
      delete newThread.id;
      const dataToAdd = formData.create({
        ...newThread,
        files,
        tourId: tour.id,
        description,
        userId,
        title,
        format: noteFormat,
        isMainMessage: true,
        reporter: foundUser?.name || 'Guest',
        SAPFields:
          noteFormat === 'SAP' ? JSON.stringify({ ...SAPData, assignee, reporter: foundUser?.email ? foundUser.email.split('@')[0] : '', createdAt, description, title }) : {},
        mentionedUsers: JSON.stringify(mentionedUsers),
        tourLink,
        isRtl,
        timezone,
      });

      const result = await PublicAPI.saveThread({
        data: dataToAdd,
      });

      resultMessage = result?.message;

      if (resultMessage === 'Success') {
        const { savedThread, reportStatus } = result;
        if (reportStatus === 'error') {
          toast.error('Failed to send SAP report to BAZAN');
        }
        if (reportStatus === 'success') {
          toast.success('SAP report sent to BAZAN successfully');
        }
        await showCaseTemp.tag.remove(savedThread.sid);
        await showcase.Mattertag.registerIcon(`thread__${savedThread.sid}`, savedThread.iconUrl).catch((e) => {
          console.error(e);
        });

        savedThread.customIcon = 'thread';

        await addThreadToSpace(savedThread);
        // setThreads((prevThreads) => [...prevThreads, savedThread]);
        setNewThread(null);
        onThreadItemSelect(savedThread);
        setIsAdding(false);
        setAddThreadClicked(false);
        controls.current.inputs.selection = null;
        socket.emit('thread_added', { ...savedThread, room: tour.slug });
      }
    }

    if (selectedThread) {
      const filteredUrls = mediaUrls.filter((urlObj) => urlObj.flaggedToDelete)?.map((urlObj) => urlObj.src);
      const dataToAdd = formData.create({
        title,
        files,
        threadId: selectedThread.id,
        description,
        userId,
        messageId,
        mediaUrls: filteredUrls,
        isMainMessage,
        mentionedUsers: JSON.stringify(mentionedUsers),
        tourLink,
        isRtl,
        timezone,
      });

      const result = await PublicAPI.saveThreadMessage({
        data: dataToAdd,
      });

      resultMessage = result?.message;
      if (resultMessage === 'Success') {
        const { savedMessage, savedTitle } = result;
        let updatedThread;
        setThreads((prevThreads) => {
          const foundThread = prevThreads.find((thread) => thread.id === savedMessage.threadId);
          if (foundThread) {
            return prevThreads.map((thread) => {
              if (thread.id === foundThread.id) {
                const messages = updateMessages(thread, savedMessage);
                updatedThread = { ...thread, title: savedTitle, messages };
                return updatedThread;
              }
              return thread;
            });
          }
          return prevThreads;
        });
        setSelectedThread((prevThread) => {
          if (updatedThread && (updatedThread?.id === prevThread?.id || !prevThread)) {
            return updatedThread;
          }
          return prevThread;
        });

        if (messageId) {
          socket.emit('message_edited', { ...result.savedMessage, savedTitle: result.savedTitle, room: tour.slug });
        } else {
          socket.emit('message_added', { ...result.savedMessage, room: tour.slug });
        }
      }
    }

    setEditMode(false);
    setOpenedMenuId(null);
    setMediaUrls([]);
    setAnchorEl(null);
    return resultMessage;
  };

  const renderMessages = useCallback(() => {
    const renderHighlightedTextWithHTML = (text) => {
      const regex = /@([\w.]+(?:\s+[\w.]+)?)/g;
      const highlightedText = text.replace(regex, (match, tag) => {
        const isCollaborator = collaborators.some((collab) => collab.name === tag.trim());
        if (isCollaborator) {
          return `<span style="color: blue;">${match}</span>`;
        } else {
          return match;
        }
      });

      return { __html: highlightedText };
    };
    return messages.map((m) => {
      const isModified = new Date(m?.createdAt).getTime() !== new Date(m?.updatedAt).getTime();
      const messageAuthor = collaborators?.find((c) => c.id === m.userId);

      const stripHtmlTags = (str: string) => {
        return str.replace(/<[^>]*>/g, '');
      };
      const descriptionText = stripHtmlTags(m?.description || '');
      const isLongText = descriptionText?.length > 100;
      const isDescExpanded = expandedMessages[m.id] || false;
      const repliesCount = selectedThread?.messages?.length ? selectedThread?.messages?.length - 1 : 0;
      const audioMessages = [];
      const imageMessages = [];

      if (m.mediaUrls) {
        m.mediaUrls?.forEach((url) => {
          if (url.includes('.wav')) {
            audioMessages.push(url);
          } else if (url.includes('.jpg') || url.includes('.png') || url.includes('.webp')) {
            imageMessages.push(`${process.env.REACT_APP_AWS}/${url}`);
          } else if (url.includes('.mp4') || url.includes('.webm') || url.includes('.ogg')) {
            imageMessages.push(`${process.env.REACT_APP_AWS}/${url}`);
          } else if (url.includes('.pdf')) {
            imageMessages.push(`${process.env.REACT_APP_AWS}/${url}`);
          }
        });
      }

      return m.isMainMessage ? (
        <Stack key={m.id} direction="row" flexWrap="wrap" width="100%" mt="18px">
          <Stack gap="10px" px="18px" pb="14px" width="100%" sx={{ background: 'white', pt: '10px' }}>
            {selectedThread?.title && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                <Typography
                  variant="h6"
                  width="100%"
                  sx={{
                    textAlign: 'left',
                    fontSize: '18px',
                    lineHeight: '28px',
                    color: '#000000',
                  }}
                >
                  {selectedThread?.title}
                </Typography>
                {m.userId === userId && (
                  <IconButton
                    onClick={() => setIsCompletionModalOpen(true)}
                    id={`message-button-${m.id}`}
                    aria-controls={isMenuOpen ? `message-menu-${m.id}` : undefined}
                    aria-haspopup="true"
                    aria-expanded={isMenuOpen ? 'true' : undefined}
                  >
                    {!isCompleted ? (
                      <i className="fa-light fa-check" style={{ color: '#12B76A', fontSize: '24px' }} />
                    ) : (
                      <i className="fa-light fa-arrow-rotate-left" style={{ color: '#12B76A', fontSize: '24px' }} />
                    )}
                  </IconButton>
                )}
              </Stack>
            )}
            <Stack width="100%" direction="row" alignItems="center" height="24px" mb="10px">
              <Stack direction="row" gap={2} alignItems="center">
                <Avatar
                  sx={{
                    height: '22px',
                    width: '22px',
                    img: { objectFit: 'contain' },
                    bgcolor: userId === messageAuthor?.id ? '#2055ff' : 'white',
                    color: userId === messageAuthor?.id ? 'white' : 'black',
                    border: '1px solid #D1D1D1',
                    fontSize: '12px',
                  }}
                >
                  {getInitials(messageAuthor?.name || 'Guest')}
                </Avatar>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#101828',
                  }}
                >
                  {messageAuthor?.name || 'Guest'}
                </Typography>
              </Stack>
              <Divider orientation="vertical" sx={{ marginX: '5px', height: '15px' }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '14px',
                  color: '#101828',
                }}
              >
                {formatDate(m.createdAt)}
              </Typography>
              {isModified && (
                <>
                  <Divider orientation="vertical" sx={{ marginX: '5px', height: '15px' }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#101828',
                    }}
                  >
                    Edited
                  </Typography>
                </>
              )}
            </Stack>
            {m?.description && (
              <Box sx={{ display: 'inline' }}>
                <Typography
                  width="100%"
                  sx={{
                    maxWidth: '400px',
                    display: 'inline',
                    textAlign: rtl ? 'right' : 'left',
                    color: '#000000',
                    fontSize: '14px',
                    lineHeight: '20px',
                    wordBreak: 'break-all',
                    whiteSpace: 'normal',
                  }}
                  dangerouslySetInnerHTML={
                    isDescExpanded || !isLongText
                      ? renderHighlightedTextWithHTML(m?.description === 'null' ? '<span></span>' : m?.description)
                      : renderHighlightedTextWithHTML(m?.description.slice(0, 100) + '...')
                  }
                />
                {isLongText && (
                  <Button
                    onClick={() => handleToggle(m.id)}
                    sx={{
                      display: 'inline',
                      textTransform: 'none',
                      padding: 0,
                      minHeight: 0,
                      minWidth: 0,
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: 'black',
                      '&:hover': {
                        color: 'black',
                        textDecoration: 'underline',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {isDescExpanded ? '...Read less' : 'Read more'}
                  </Button>
                )}
              </Box>
            )}
            <Divider sx={{ width: '100%' }} />
            {!!audioMessages?.length &&
              audioMessages.map((url, i) => {
                return (
                  <Fragment key={uniqueId()}>
                    <WaveSurferPlayer
                      url={`${process.env.REACT_APP_AWS}/${url}`}
                      waveColor="#D0D5DD"
                      progressColor="#2055FF"
                      barWidth={2}
                      barGap={2}
                      barRadius={4}
                      barHeight={10}
                      wavefHeight={33}
                      playPauseButtonSx={{ color: '#2055FF', padding: 0, height: '30px' }}
                      containerSx={{
                        padding: '10px',
                        height: '70px',
                        maxHeight: '70px',
                        width: '100%',
                        gap: '6px',
                        background: '#F9FAFB',
                        border: '1px solid #EAECF0',
                        borderRadius: '8px',
                      }}
                    />
                  </Fragment>
                );
              })}
            {!!imageMessages?.length && (
              <Box width="320px" margin="0 auto">
                <Gallery
                  items={imageMessages}
                  settings={{
                    fullScreen: false,
                    imageHeight: 252,
                    dotsColor: '#D0D5DD',
                    thumbnailIconBackground: '#F4F5F2',
                    thumbnailBorderColor: '#D1D1D1',
                    thumbnailSize: 70,
                    fullscreenByClick: true,
                    alternativeArrows: false,
                    thumbnails: imageMessages.length > 1 ? true : false,
                    dots: imageMessages.length > 1 ? true : false,
                    isNotesGallery: true,
                  }}
                />
              </Box>
            )}
          </Stack>
          {editMode && m.id === openedMenuId && userId && !isGuest && (
            <Stack width="100%" px="18px" sx={{ background: '#F1F1F1' }}>
              <NoteForm
                rtl={rtl}
                userId={userId}
                handleAdd={handleAdd}
                files={files}
                setFiles={setFiles}
                noteFormat={noteFormat}
                setNoteFormat={setNoteFormat}
                isNewThread={false}
                editMode={editMode}
                setEditMode={setEditMode}
                mediaUrls={mediaUrls.filter((objUrl) => !objUrl.flaggedToDelete)}
                setMediaUrls={setMediaUrls}
                collaborators={collaborators}
                foundUser={foundUser}
                isMainMessage={m.isMainMessage}
                noteTitle={selectedThread.title}
                currentMessage={m}
                onCloseDrawer={onCloseDrawer}
              />
            </Stack>
          )}
          {selectedThread?.format === 'SAP' && (
            <Stack sx={{ paddingX: '18px', paddingBottom: '18px', width: '100%', background: 'white' }}>
              <SAPDetails SAPFields={selectedThread.SAPFields} />
            </Stack>
          )}
          <Box sx={{ background: 'transparent', padding: '18px', paddingBottom: '0', width: '100%' }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >{`${repliesCount} ${repliesCount === 1 ? 'Reply' : 'Replies'}`}</Typography>
          </Box>
        </Stack>
      ) : (
        <Stack key={m.id} direction="row" flexWrap="wrap" width="100%" px="18px">
          <Stack
            sx={{ display: 'flex', flexDirection: 'column', gap: '8px', background: 'white', border: '1px solid #EAECF0', borderRadius: '8px', width: '100%', padding: '14px' }}
          >
            <Stack width="100%" direction="row" alignItems="center" gap={1} py={1}>
              <Avatar
                sx={{
                  height: '40px',
                  width: '40px',
                  img: { objectFit: 'contain' },
                  bgcolor: userId === messageAuthor?.id ? '#2055ff' : 'white',
                  color: userId === messageAuthor?.id ? 'white' : 'black',
                  border: '1px solid #D1D1D1',
                }}
              >
                {getInitials(messageAuthor?.name || 'Guest')}
              </Avatar>
              <Stack direction="column">
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#101828',
                    fontWeight: '550',
                  }}
                >
                  {messageAuthor?.name || 'Guest'}
                </Typography>
                <Stack direction="row">
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '14px',
                      color: '#667085',
                    }}
                  >
                    {formatDate(m.updatedAt)}
                  </Typography>
                  {isModified && (
                    <>
                      <Divider orientation="vertical" sx={{ marginX: '5px', height: '15px' }} />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#667085',
                        }}
                      >
                        Edited
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>
              <Stack direction="row" justifyContent="center" alignItems="center" gap={1} ml="auto">
                {m?.userId === userId && (
                  <EditMenu
                    selectedThread={selectedThread}
                    message={m}
                    setMediaUrls={setMediaUrls}
                    anchorEl={anchorEl}
                    setAnchorEl={setAnchorEl}
                    setOpenedMenuId={setOpenedMenuId}
                    isMenuOpen={isMenuOpen}
                    setEditMode={setEditMode}
                    setIsDeleteNoteModalOpen={setIsDialogOpen}
                    handleEdit={handleEdit}
                  />
                )}
              </Stack>
            </Stack>
            {m?.description && (
              <Box sx={{ display: 'inline' }}>
                <Typography
                  variant="body1"
                  width="100%"
                  sx={{
                    maxWidth: '400px',
                    display: 'inline',
                    textAlign: rtl ? 'right' : 'left',
                    color: '#101828',
                    fontSize: '14px',
                    lineHeight: '20px',
                    wordBreak: 'break-all',
                    whiteSpace: 'normal',
                  }}
                  dangerouslySetInnerHTML={
                    isDescExpanded || !isLongText
                      ? renderHighlightedTextWithHTML(m?.description === 'null' ? '<span></span>' : m?.description)
                      : renderHighlightedTextWithHTML(m?.description.slice(0, 100) + '...')
                  }
                />
                {isLongText && (
                  <Button
                    onClick={() => handleToggle(m.id)}
                    sx={{
                      display: 'inline',
                      textTransform: 'none',
                      padding: 0,
                      minHeight: 0,
                      minWidth: 0,
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: 'black',
                      '&:hover': {
                        color: 'black',
                        textDecoration: 'underline',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {isDescExpanded ? '...Read less' : 'Read more'}
                  </Button>
                )}
              </Box>
            )}
            {!!audioMessages?.length &&
              audioMessages.map((url, i) => {
                return (
                  <Fragment key={uniqueId()}>
                    <WaveSurferPlayer
                      url={`${process.env.REACT_APP_AWS}/${url}`}
                      waveColor="#D0D5DD"
                      progressColor="#2055FF"
                      barWidth={2}
                      barGap={2}
                      barRadius={4}
                      barHeight={10}
                      wavefHeight={33}
                      playPauseButtonSx={{ color: '#2055FF', padding: 0, height: '30px' }}
                      containerSx={{
                        padding: '10px',
                        height: '70px',
                        maxHeight: '70px',
                        width: '100%',
                        gap: '6px',
                        background: '#F9FAFB',
                        border: '1px solid #EAECF0',
                        borderRadius: '8px',
                      }}
                    />
                  </Fragment>
                );
              })}
            {!!imageMessages?.length && (
              <Box width="320px" margin="0 auto">
                <Gallery
                  items={imageMessages}
                  settings={{
                    fullScreen: false,
                    imageHeight: 252,
                    dotsColor: '#D0D5DD',
                    thumbnailIconBackground: '#F4F5F2',
                    thumbnailBorderColor: '#D1D1D1',
                    thumbnailSize: 70,
                    fullscreenByClick: true,
                    alternativeArrows: false,
                    thumbnails: imageMessages.length > 1 ? true : false,
                    dots: imageMessages.length > 1 ? true : false,
                    isNotesGallery: true,
                  }}
                />
              </Box>
            )}
          </Stack>
          {editMode && m.id === openedMenuId && userId && !isGuest && (
            <Stack width="100%" sx={{ background: '#F1F1F1CC' }}>
              <NoteForm
                rtl={rtl}
                userId={userId}
                handleAdd={handleAdd}
                files={files}
                setFiles={setFiles}
                noteFormat={noteFormat}
                setNoteFormat={setNoteFormat}
                isNewThread={false}
                editMode={editMode}
                setEditMode={setEditMode}
                mediaUrls={mediaUrls.filter((objUrl) => !objUrl.flaggedToDelete)}
                setMediaUrls={setMediaUrls}
                collaborators={collaborators}
                foundUser={foundUser}
                currentMessage={m}
                onCloseDrawer={onCloseDrawer}
              />
            </Stack>
          )}
        </Stack>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    messages,
    newThread,
    threadUser?.name,
    isMenuOpen,
    mediaUrls,
    files,
    isTourAdmin,
    foundUser?.id,
    expandedMessages,
    selectedThread?.title,
    selectedThread?.messages,
    isCompleted,
  ]);

  const completeNote = async (noteId) => {
    await onCompleteNote(noteId);
    setIsCompleted(!selectedThread?.isCompleted);
  };

  const getTourLink = () => {
    let linkValue = getBaseURL();

    linkValue = getLocation();

    const segmants = window.location.href.split('/');
    const slug = segmants[segmants.length - 1].split('?')[0];
    if (tour.slug !== slug) {
      const params = linkValue.split('?')[1];
      linkValue = `${linkValue
        .split('/')
        .map((part, index) => (index === 4 ? tour.slug : part))
        .join('/')}${params ? `?${params}` : ''}`;
    }
    return linkValue;
  };

  return (
    <Stack direction="column" justifyContent={!!newThread || !userId || isGuest ? 'flex-start' : 'center'} flexWrap="wrap" alignItems="space-between" flexGrow={editMode ? 0 : 1}>
      {!!newThread && <NoteReporterBar name={foundUser?.name} editMode={editMode} isNewThread={!!newThread} />}
      <Stack direction="column" gap={2}>
        {renderMessages()}
      </Stack>
      {isLoading && (
        <Stack width="100%" alignItems="center" justifyContent="center" height="100%" position="absolute" sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 100 }}>
          <CircularProgress />
        </Stack>
      )}
      {!editMode && userId && !isGuest && (
        <Stack
          px="18px"
          sx={{
            background: '#F1F1F1',
            marginTop: !!newThread ? 0 : 'auto',
            flexGrow: !!newThread ? 1 : 0,
            ...(newThread
              ? {}
              : {
                  position: 'sticky',
                  bottom: 0,
                  maxHeight: '700px',
                  zIndex: '1600',
                }),
          }}
        >
          <NoteForm
            rtl={rtl}
            handleAdd={handleAdd}
            files={files}
            setFiles={setFiles}
            noteFormat={noteFormat}
            setNoteFormat={setNoteFormat}
            isNewThread={!!newThread}
            userId={userId}
            collaborators={collaborators}
            foundUser={foundUser}
            SAPData={SAPData}
            setSAPData={setSAPData}
            onGetSAPOptions={handleSAPFieldOptions}
            isSAPActive={isSAPActive}
            onCloseDrawer={onCloseDrawer}
          />
        </Stack>
      )}
      {isCompletionModalOpen && (
        <CompleteNoteModal
          isCompletionModalOpen={isCompletionModalOpen}
          handleClose={() => setIsCompletionModalOpen(false)}
          selectedNote={selectedThread}
          foundUser={foundUser}
          completeNote={completeNote}
        />
      )}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          '& .MuiDialog-paper': { background: 'transparent', borderRadius: '12px' },
        }}
      >
        <Stack sx={{ maxWidth: '400px', background: 'white', borderRadius: '12px' }}>
          <DialogContent>
            <Box display="flex" alignItems="start" justifyContent="flex-start" paddingBottom={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px', borderRadius: '50%', background: '#FEF3F2' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '35px', height: '35px', borderRadius: '50%', background: '#FEE4E2' }}>
                  <Icon.Delete style={{ color: 'transparent', fontSize: 20 }} />
                </Box>
              </Box>
            </Box>
            <Typography variant="h6" align="left">
              Delete this reply?
            </Typography>
            <Typography align="left" sx={{ color: '#667085', fontSize: '14px', fontWeight: '400' }}>
              Are you sure you want to delete this reply? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={2} justifyContent="space-between" width="100%" padding={2}>
              <Button
                variant="outlined"
                onClick={() => setIsDialogOpen(false)}
                style={{
                  color: '#344054',
                  flex: 1,
                  marginRight: 12,
                  border: '1px solid #D0D5DD',
                  borderRadius: 8,
                  height: 48,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleDelete}
                color="primary"
                style={{
                  flex: 1,
                  marginLeft: 12,
                  borderRadius: 8,
                  height: 48,
                  background: '#D92D20',
                }}
              >
                Delete
              </Button>
            </Stack>
          </DialogActions>
        </Stack>
      </Dialog>
      {isDeleteNoteModalOpen && (
        <DeleteNoteModal
          isDeleteNoteModalOpen={isDeleteNoteModalOpen}
          setIsDeleteNoteModalOpen={setIsDeleteNoteModalOpen}
          setThreads={setThreads}
          selectedThread={selectedThread}
          setSelectedThread={setSelectedThread}
          threads={threads}
          tourSlug={tour.slug}
          userId={foundUser.id}
          removeThreadFromSpace={removeThreadFromSpace}
        />
      )}
    </Stack>
  );
};
